/*
 * Copyright (c) 2018 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain proprietary information of OCLC Online Computer
 * Library Center, Inc. and shall not be disclosed in whole or in any part to any third party or used by any person
 * for any purpose, without written consent of OCLC, Inc.  Duplication of any portion of these materials shall include
 * this notice.
 */
import React, {Component, PureComponent} from 'react';
import PropTypes from 'prop-types';

import NavigationPrompt from 'react-router-navigation-prompt';
import DirtyFormConfirmation from './DirtyFormConfirmation';
import DomainApplicationErrorView from './DomainApplicationErrorView';
import ForbiddenView from '../ForbiddenView';
import {MainView} from '../MainView';
import styles from './DomainApplicationFrame.css';
import LoadingIndicator from '../LoadingIndicator';


export class DomainApplicationFrame extends Component {
  static defaultProps = {

    width: '100%',
    height: '100%',

    frameBorder: 0,
    paddingBottom: 50,

    loadingDelay: 1000
  };

  static propTypes = {

    /**
     * Width of the content frame.  Defaults to 100%.
     */
    width: PropTypes.string,

    /**
     * <strong>Initial</strong> height of the content frame.  Defaults to 100% of the content, but will resize
     * automatically as the frame's content shrinks and grows.
     */
    height: PropTypes.string,

    /**
     * Frame border, useful for debugging the size of the frame.  Always '0' (hidden) in production.
     */
    frameBorder: PropTypes.number,

    /**
     * Amount of padding to add to the bottom of the iframe to ensure that all content is visible.
     */
    paddingBottom: PropTypes.number,

    /**
     * Location object injected by React Router.
     */
    location: PropTypes.shape({
      pathname: PropTypes.string,
      key: PropTypes.string
    }),

    /**
     * Milliseconds to delay before displaying a loading indicator.
     */
    loadingDelay: PropTypes.number.isRequired,

    /**
     * Callback invoked to inform parent components that the domain application switched context institution.
     */
    onChangeInstitution: PropTypes.func.isRequired,

    /**
     * Callback invoked to syncrhonize the domain application's display with the application menu.
     */
    onNavigateToApplication: PropTypes.func.isRequired
  };

  constructor (props) {
    super(props);
    this.state = {};
  }

  render () {
    const {status} = this.state.error || {};
    let content = [];

    if (status === 403) {
      content = <ForbiddenView {...this.state.error} />;
    } else if (status >= 400 && status <= 599) {
      content = [
        <DomainApplicationErrorView key={`error-view-${status}`} {...this.state.error} />,
        this.renderFrame()
      ];
    } else {
      content = this.renderFrame();
    }

    return (
      <div className={styles.applicationFrameContainer}>
        {content}
      </div>
    );
  }

  renderFrame = () => {
    let frameUrl;

    const url = this.props.location.pathname.replace('/apps', '');
    if (url) {
      const search = new URLSearchParams(this.props.location.search);
      frameUrl = `${window.location.origin}${process.env.PUBLIC_URL}/module${url}`;
      frameUrl += (search.toString() ? `?${search.toString()}` : '');
    }

    return (

      <div key='domain-application-content'>
        {frameUrl == null &&  <MainView />}
        {this.state.loading && frameUrl != null ? <CenteredLoadingIndicator /> : null}

        <div style={{display: this.state.loading ? 'none' : 'block'}}>
          <IFrame frameUrl={frameUrl}
             width={this.props.width}
             height={this.props.height}
             frameBorder={this.props.frameBorder}
             location={this.props.location}
             iframeRef={this.iframeRef}
          />
        </div>
        <NavigationPrompt when={this.state.dirtyForm === true}
                          afterCancel={this.afterCancel}
                          disableNative={true}>
          {({onConfirm, onCancel}) => (
            <DirtyFormConfirmation onCancel={onCancel} onConfirm={onConfirm} />
          )}
        </NavigationPrompt>
      </div>
    );
  };

  afterCancel = () => {
    if (this.props.location.state) {
      const {pageId, applicationId} = this.props.location.state;
      this.props.onNavigateToApplication({pageId: pageId, applicationId: applicationId});

      document.activeElement && document.activeElement.blur();
    }
  };

  /**
   * Stores a reference to the iframe DOM node that loads the domain application content.
   *
   * @param frame DOM node
   */
  iframeRef = (frame) => {this.iframe = frame;};

  /**
   * Turns on the loading indicator.
   */
  turnOnLoadingIndicator = () => {
    this.turnOffLoadingIndicator();

    this.loadingTimerId = setTimeout(() => {
      if (!this.state.loading) {
        this.setState({loading: true});
      }
    }, this.props.loadingDelay);
  }

  /**
   * Turn off the loading indicator.
   */
  turnOffLoadingIndicator = () => {
    if (this.loadingTimerId) {
      clearTimeout(this.loadingTimerId);
      this.loadingTimerId = null;
    }
    this.setState({loading: false});
  }

  componentWillUnmount () {
    window.removeEventListener('message', this.handleDomainAppMessage);
  }

  componentDidMount () {
    window.addEventListener('message', this.handleDomainAppMessage);

    this.turnOnLoadingIndicator();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {

    if (prevProps.location !== this.props.location) {
      this.turnOnLoadingIndicator();
    }
  }

  static getDerivedStateFromProps = (props, state) => {
    let newState = {};

    if (state.lastLocation !== props.location) {
      newState = {
        ...newState,
        lastLocation: props.location,
        dirtyForm: false,
        error: null
      };
    }
    return Object.keys(newState).length > 0 ? newState : null;
  }

  /**
   * Updates the domain application iframe with data received from the domain application frame.
   *
   * @param e message event received from the domain application.
   */
  handleDomainAppMessage = (e) => {
    const action = e.data || {};
    const payload = action ? action.payload : {};
    const payloadSource = payload ? payload.source : {};
    const source = action.source || payloadSource;
    if (source === '@curry-domainapp-page') {
      switch (e.data.type) {
        case 'DOMContentLoaded':
          // reset the dirty form flag
          this.setState({dirtyForm: false});
          e.data.menu && this.props.onNavigateToApplication({pageId: e.data.menu});
          window.scrollTo(0,0);
          this.turnOffLoadingIndicator();
          // observe, no break.  we fall through
        case 'load':
        case 'dom-mutation':
          const newHeight = (e.data.scrollHeight + this.props.paddingBottom) + 'px';
          if (this.iframe) {
            this.iframe.height = this.iframe.style.height = newHeight;
          }
          break;
        case 'error':
          this.setState({error: e.data.error});
          break;
        case 'change-context-institution':
          const {institution} = e.data;

          (async () => {
            await this.props.onChangeInstitution(institution);
            const message = {
              source: '@curry-application',
              type: 'context-institution-changed',
              institution: institution
            };
            e.source.postMessage(message, '*');
          })();

          break;
        case 'dirty-form-detection':
          this.setState({'dirtyForm': true});
          break;
        case 'disable-dirty-form-detection':
          this.setState({'dirtyForm': false});
          break;
        default:
          console.warn('Unrecognized event received from domain application', action);
          break;
      }
    }
  };
}

/**
 * Lightweight wrapper around the <LoadingIndicator/> that vertically centers the component.
 *
 * @param props
 * @return {*}
 * @constructor
 */
const CenteredLoadingIndicator = (props) => {
  return (
    <div className={styles.loadingIndicator}>
      <LoadingIndicator />
    </div>
  );
};


/**
 * Lightweight wrapper around the <iframe/> tag.  Defining this as it's own component gives greater control
 * over when the iframe should render.
 */
export class IFrame extends PureComponent {
  render() {
    return (
        <iframe src={this.props.frameUrl || 'about:blank'}
                style={{display: 'block', width: this.props.width, height: this.props.height}}
                width={this.props.width}
                height={this.props.height}
                role="application"
                title="domain application"
                id="domain-application-frame"
                frameBorder={this.props.frameBorder}
                scrolling="no"
                allowFullScreen={true}
                ref={this.props.iframeRef}
                key={new Date()}
        />
    );
  }
}

export default DomainApplicationFrame;



// WEBPACK FOOTER //
// ./src/DomainApplicationView/DomainApplicationFrame.js